/**
 * SIP Trunk Form Page - Create/Edit SIP trunks.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import DialerSipIcon from '@mui/icons-material/DialerSip';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
  OutlinedInput,
  IconButton,
  Paper,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  Add,
  CallReceived,
  CallMade,
  DeleteOutline,
  Save,
  HelpOutline,
  Tune,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import ButtonTabs, { type ButtonTab } from '../components/ButtonTabs';
import DetailHeader from '../components/DetailHeader';
import SipTrunkInboundTab from '../components/sipTrunks/SipTrunkInboundTab';
import usePageTitle from '../hooks/usePageTitle';

interface FormOptions {
  codecs: { value: string; label: string }[];
  transports: { value: string; label: string }[];
  mediaEncryption: { value: string; label: string }[];
  inboundContexts: { value: string; label: string }[];
  classificationRulePhases: { value: string; label: string }[];
  preGateOutcomes: { value: string; label: string }[];
  providerResultParsers: { value: string; label: string }[];
}

const DEFAULT_FORM_OPTIONS: FormOptions = {
  codecs: [
    { value: 'ulaw', label: 'G.711 u-law (ulaw)' },
    { value: 'alaw', label: 'G.711 A-law (alaw)' },
  ],
  transports: [
    { value: 'udp', label: 'UDP' },
    { value: 'tcp', label: 'TCP' },
    { value: 'tls', label: 'TLS' },
  ],
  mediaEncryption: [
    { value: 'disabled', label: 'Disabled' },
    { value: 'allowed', label: 'Allowed' },
    { value: 'required', label: 'Required' },
  ],
  inboundContexts: [
    { value: 'from-trunk', label: 'From Trunk (default)' },
    { value: 'from-external', label: 'From External' },
    { value: 'from-pstn', label: 'From PSTN' },
    { value: 'inbound', label: 'Inbound' },
    { value: 'default', label: 'Default' },
  ],
  classificationRulePhases: [
    { value: 'pre_gate', label: 'Pre-Gate' },
  ],
  preGateOutcomes: [
    { value: 'human', label: 'Human' },
    { value: 'voicemail', label: 'Voicemail' },
    { value: 'busy', label: 'Busy' },
    { value: 'no_answer', label: 'No Answer' },
    { value: 'congestion', label: 'Congestion' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'declined', label: 'Declined' },
    { value: 'non_human_rejected', label: 'Non-human Rejected' },
    { value: 'invalid_number', label: 'Invalid Number' },
    { value: 'unreachable', label: 'Unreachable' },
  ],
  providerResultParsers: [
    { value: 'none', label: 'None' },
    { value: 'cdc_result', label: 'CDC result' },
  ],
};

interface ClassificationRuleFormData {
  id?: string;
  localId: string;
  priority: string;
  phase: string;
  sipStatusCode: string;
  hangupCauseCode: string;
  dialStatus: string;
  preGateOutcome: string;
}

interface FormData {
  name: string;
  description: string;
  host: string;
  port: number;
  transport: string;
  username: string;
  password: string;
  fromUser: string;
  fromDomain: string;
  machineDetectionStrategy: string;
  providerResultCaptureEnabled: boolean;
  providerResultHeaderName: string;
  providerResultParser: string;
  codecs: string[];
  mediaEncryption: string;
  inboundContext: string;
  callerId: string;
  techPrefix: string;
  removeDigits: string;
  isActive: boolean;
  isGlobal: boolean;
  classificationRules: ClassificationRuleFormData[];
}

/**
 * Build a stable local rule row for create/edit flows before the backend assigns a public id.
 */
function buildClassificationRuleDraft(): ClassificationRuleFormData {
  return {
    localId: crypto.randomUUID(),
    priority: '100',
    phase: 'pre_gate',
    sipStatusCode: '',
    hangupCauseCode: '',
    dialStatus: '',
    preGateOutcome: 'non_human_rejected',
  };
}

const initialFormData: FormData = {
  name: '',
  description: '',
  host: '',
  port: 5060,
  transport: 'udp',
  username: '',
  password: '',
  fromUser: '',
  fromDomain: '',
  machineDetectionStrategy: 'provider_signaling',
  providerResultCaptureEnabled: false,
  providerResultHeaderName: '',
  providerResultParser: 'none',
  codecs: ['ulaw', 'alaw'],
  mediaEncryption: 'disabled',
  inboundContext: 'from-trunk',
  callerId: '',
  techPrefix: '',
  removeDigits: '',
  isActive: true,
  isGlobal: false,
  classificationRules: [],
};

export default function SipTrunkFormPage() {
  const { t } = useTranslation(['pages', 'common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const isEditing = id && id !== 'new';

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [trunkId, setTrunkId] = useState<string | null>(null);
  const descriptor = formData.name || trunkId || undefined;
  usePageTitle(t('pages:sipTrunks.title'), descriptor);
  const [options, setOptions] = useState<FormOptions>(DEFAULT_FORM_OPTIONS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const rawFormTab = searchParams.get('tab');
  const activeFormTab = rawFormTab === 'outbound'
    ? 'outbound'
    : rawFormTab === 'inbound' && isEditing && !formData.isGlobal
      ? 'inbound'
      : 'general';

  const fetchOptions = useCallback(async () => {
    try {
      const response = await api.get('/api/sip-trunks/options/');
      setOptions({
        codecs: response.data?.codecs ?? DEFAULT_FORM_OPTIONS.codecs,
        transports: response.data?.transports ?? DEFAULT_FORM_OPTIONS.transports,
        mediaEncryption: response.data?.mediaEncryption ?? DEFAULT_FORM_OPTIONS.mediaEncryption,
        inboundContexts: response.data?.inboundContexts ?? DEFAULT_FORM_OPTIONS.inboundContexts,
        classificationRulePhases: response.data?.classificationRulePhases ?? DEFAULT_FORM_OPTIONS.classificationRulePhases,
        preGateOutcomes: response.data?.preGateOutcomes ?? DEFAULT_FORM_OPTIONS.preGateOutcomes,
        providerResultParsers: response.data?.providerResultParsers ?? DEFAULT_FORM_OPTIONS.providerResultParsers,
      });
    } catch (err) {
      console.error('Error fetching options:', err);
    }
  }, []);

  const fetchTrunk = useCallback(async () => {
    if (!isEditing) return;

    setLoading(true);
    setTrunkId(null);
    try {
      const response = await api.get(`/api/sip-trunks/${id}/`);
      const trunk = response.data;
      setTrunkId(trunk.id || null);
      setFormData({
        name: trunk.name || '',
        description: trunk.description || '',
        host: trunk.host || '',
        port: trunk.port || 5060,
        transport: trunk.transport || 'udp',
        username: trunk.username || '',
        password: '', // Don't load password
        fromUser: trunk.fromUser || '',
        fromDomain: trunk.fromDomain || '',
        machineDetectionStrategy: trunk.machineDetectionStrategy || 'provider_signaling',
        providerResultCaptureEnabled: trunk.providerResultCaptureEnabled ?? false,
        providerResultHeaderName: trunk.providerResultHeaderName || '',
        providerResultParser: trunk.providerResultParser || 'none',
        codecs: trunk.codecs || ['ulaw', 'alaw'],
        mediaEncryption: trunk.mediaEncryption || 'disabled',
        inboundContext: trunk.inboundContext || 'from-trunk',
        callerId: trunk.callerId || '',
        techPrefix: trunk.techPrefix || '',
        removeDigits: trunk.removeDigits || '',
        isActive: trunk.isActive ?? true,
        isGlobal: trunk.isGlobal ?? false,
        classificationRules: (trunk.classificationRules || []).map((rule: Record<string, unknown>) => ({
          id: String(rule.id || ''),
          localId: String(rule.id || crypto.randomUUID()),
          priority: String(rule.priority ?? '100'),
          phase: String(rule.phase || 'pre_gate'),
          sipStatusCode: rule.sipStatusCode == null ? '' : String(rule.sipStatusCode),
          hangupCauseCode: rule.hangupCauseCode == null ? '' : String(rule.hangupCauseCode),
          dialStatus: String(rule.dialStatus || ''),
          preGateOutcome: String(rule.preGateOutcome || 'non_human_rejected'),
        })),
      });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setError(error?.response?.data?.detail || t('pages:sipTrunks.errors.loading'));
    } finally {
      setLoading(false);
    }
  }, [id, isEditing, t]);

  useEffect(() => {
    fetchOptions();
    fetchTrunk();
  }, [fetchOptions, fetchTrunk]);

  useEffect(() => {
    const currentTab = searchParams.get('tab');
    const isSupportedTab = currentTab === 'general' || currentTab === 'outbound' || currentTab === 'inbound';
    const inboundUnavailable = currentTab === 'inbound' && (!isEditing || formData.isGlobal);
    if (!currentTab || (isSupportedTab && !inboundUnavailable)) {
      return;
    }
    const params = new URLSearchParams(searchParams);
    params.delete('tab');
    setSearchParams(params, { replace: true });
  }, [formData.isGlobal, isEditing, searchParams, setSearchParams]);

  const handleChange = (field: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string | string[]>
  ) => {
    const target = e.target as HTMLInputElement;
    // Only use 'checked' for actual checkboxes/switches
    const value = target.type === 'checkbox' ? target.checked : target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCodecsChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      codecs: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleRuleChange = (
    localId: string,
    field: keyof Omit<ClassificationRuleFormData, 'id' | 'localId'>,
  ) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const nextValue = event.target.value;
    setFormData((prev) => ({
      ...prev,
      classificationRules: prev.classificationRules.map((rule) => (
        rule.localId === localId ? { ...rule, [field]: nextValue } : rule
      )),
    }));
  };

  const handleAddClassificationRule = () => {
    setFormData((prev) => ({
      ...prev,
      classificationRules: [...prev.classificationRules, buildClassificationRuleDraft()],
    }));
  };

  const handleRemoveClassificationRule = (localId: string) => {
    setFormData((prev) => ({
      ...prev,
      classificationRules: prev.classificationRules.filter((rule) => rule.localId !== localId),
    }));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const classificationRules = formData.classificationRules.map((rule) => ({
        ...(rule.id ? { id: rule.id } : {}),
        priority: Number(rule.priority || 100),
        phase: rule.phase,
        sipStatusCode: rule.sipStatusCode ? Number(rule.sipStatusCode) : null,
        hangupCauseCode: rule.hangupCauseCode ? Number(rule.hangupCauseCode) : null,
        dialStatus: rule.dialStatus.trim().toUpperCase(),
        preGateOutcome: rule.preGateOutcome,
      }));
      const payload = {
        ...formData,
        port: Number(formData.port || 5060),
        classificationRules,
        // Only send password if it's not empty
        password: formData.password || undefined,
      };

      if (isEditing) {
        await api.patch(`/api/sip-trunks/${id}/`, payload);
        setSuccess(t('pages:sipTrunks.success.updated'));
      } else {
        await api.post('/api/sip-trunks/', payload);
        setSuccess(t('pages:sipTrunks.success.created'));
        setTimeout(() => navigate('/sip-trunks'), 1500);
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: Record<string, string[]> } };
      const errorData = error?.response?.data;
      if (errorData && typeof errorData === 'object') {
        const firstError = Object.values(errorData)[0];
        setError(Array.isArray(firstError) ? firstError[0] : String(firstError));
      } else {
        setError(t('pages:sipTrunks.errors.saving'));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const headerTitle = isEditing
    ? formData.name || t('pages:sipTrunks.edit')
    : t('pages:sipTrunks.create');

  const breadcrumbs = [
    { label: t('common:menu.sipTrunks'), to: '/sip-trunks' },
    { label: isEditing ? t('pages:sipTrunks.edit') : t('pages:sipTrunks.create') },
  ];

  const headerActions = (
    <Button
      type="submit"
      variant="contained"
      startIcon={
        saving ? <CircularProgress size={20} color="inherit" /> : <Save />
      }
      disabled={saving}
    >
      {saving ? t('common:actions.saving') : t('common:actions.save')}
    </Button>
  );

  const formTabs: ButtonTab[] = [
    {
      id: 'general',
      label: t('pages:sipTrunks.form.tabs.general'),
      icon: <Tune fontSize="small" />,
    },
    {
      id: 'outbound',
      label: t('pages:sipTrunks.form.tabs.outbound'),
      icon: <CallMade fontSize="small" />,
    },
    {
      id: 'inbound',
      label: t('pages:sipTrunks.form.tabs.inbound'),
      icon: <CallReceived fontSize="small" />,
      disabled: !isEditing || formData.isGlobal,
    },
  ];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <DetailHeader
        title={headerTitle}
        sectionIcon={<DialerSipIcon sx={{ fontSize: 32, color: 'primary.main' }} />}
        entityId={trunkId}
        breadcrumbs={breadcrumbs}
        actions={headerActions}
        backHref="/sip-trunks"
      />

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <ButtonTabs
        tabs={formTabs}
        defaultTab="general"
        tabParam="tab"
        useUrl
        renderContent={false}
        sx={{ mb: 2 }}
      />

      <Box sx={{ width: '100%' }}>
        <Box
          role="tabpanel"
          hidden={activeFormTab !== 'general'}
          sx={{
            display: activeFormTab === 'general' ? 'flex' : 'none',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('pages:sipTrunks.form.basicInfo')}
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('pages:sipTrunks.form.name')}
                  value={formData.name}
                  onChange={handleChange('name')}
                  placeholder={t('pages:sipTrunks.form.namePlaceholder')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label={t('pages:sipTrunks.form.isActive')}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label={t('pages:sipTrunks.form.description')}
                  value={formData.description}
                  onChange={handleChange('description')}
                  placeholder={t('pages:sipTrunks.form.descriptionPlaceholder')}
                />
              </Grid>
            </Grid>
          </CardContent>
          </Card>
        </Box>

        <Box
          role="tabpanel"
          hidden={activeFormTab !== 'outbound'}
          sx={{
            display: activeFormTab === 'outbound' ? 'flex' : 'none',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('pages:sipTrunks.form.connection')}
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('pages:sipTrunks.form.host')}
                  value={formData.host}
                  onChange={handleChange('host')}
                  placeholder="sip.ai_agent_provider.com"
                  helperText={t('pages:sipTrunks.form.hostHelper')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('pages:sipTrunks.form.port')}
                  value={formData.port}
                  onChange={handleChange('port')}
                  inputProps={{ min: 1, max: 65535 }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('pages:sipTrunks.form.transport')}</InputLabel>
                  <Select
                    value={formData.transport}
                    onChange={handleChange('transport')}
                    label={t('pages:sipTrunks.form.transport')}
                  >
                    {options?.transports.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
          </Card>

          <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
              <Typography variant="h6">
                {t('pages:sipTrunks.form.authentication')}
              </Typography>
              <Tooltip title={t('pages:sipTrunks.form.authenticationHelper')} arrow>
                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('pages:sipTrunks.form.username')}
                  value={formData.username}
                  onChange={handleChange('username')}
                  placeholder={t('pages:sipTrunks.form.usernamePlaceholder')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  type="password"
                  label={t('pages:sipTrunks.form.password')}
                  value={formData.password}
                  onChange={handleChange('password')}
                  placeholder={isEditing ? t('pages:sipTrunks.form.passwordUnchanged') : ''}
                  helperText={isEditing ? t('pages:sipTrunks.form.passwordHelper') : ''}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('pages:sipTrunks.form.fromUser')}
                  value={formData.fromUser}
                  onChange={handleChange('fromUser')}
                  placeholder={t('pages:sipTrunks.form.fromUserPlaceholder')}
                  helperText={t('pages:sipTrunks.form.fromUserHelper')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('pages:sipTrunks.form.fromDomain')}
                  value={formData.fromDomain}
                  onChange={handleChange('fromDomain')}
                  placeholder={t('pages:sipTrunks.form.fromDomainPlaceholder')}
                  helperText={t('pages:sipTrunks.form.fromDomainHelper')}
                />
              </Grid>
            </Grid>
          </CardContent>
          </Card>

          <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
              <Typography variant="h6">
                {t('pages:sipTrunks.form.machineDetection')}
              </Typography>
              <Tooltip title={t('pages:sipTrunks.form.machineDetectionHelper')} arrow>
                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  {t('pages:sipTrunks.form.machineDetectionStrategyFixed')}
                </Alert>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  {t('pages:sipTrunks.form.classificationRulesProviderHint')}
                </Alert>
              </Grid>
            </Grid>
          </CardContent>
          </Card>

          <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
              <Typography variant="h6">
                {t('pages:sipTrunks.form.providerResultCapture')}
              </Typography>
              <Tooltip title={t('pages:sipTrunks.form.providerResultCaptureHelper')} arrow>
                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  {t('pages:sipTrunks.form.providerResultCaptureOnlyNotice')}
                </Alert>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.providerResultCaptureEnabled}
                      onChange={(event) => setFormData({
                        ...formData,
                        providerResultCaptureEnabled: event.target.checked,
                        providerResultParser: event.target.checked ? formData.providerResultParser : 'none',
                      })}
                    />
                  }
                  label={t('pages:sipTrunks.form.providerResultCaptureEnabled')}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {t('pages:sipTrunks.form.providerResultCaptureEnabledHelper')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  disabled={!formData.providerResultCaptureEnabled}
                  label={t('pages:sipTrunks.form.providerResultHeaderName')}
                  value={formData.providerResultHeaderName}
                  onChange={handleChange('providerResultHeaderName')}
                  placeholder={t('pages:sipTrunks.form.providerResultHeaderNamePlaceholder')}
                  helperText={t('pages:sipTrunks.form.providerResultHeaderNameHelper')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth disabled={!formData.providerResultCaptureEnabled}>
                  <InputLabel>{t('pages:sipTrunks.form.providerResultParser')}</InputLabel>
                  <Select
                    value={formData.providerResultParser}
                    onChange={handleChange('providerResultParser')}
                    label={t('pages:sipTrunks.form.providerResultParser')}
                  >
                    {options?.providerResultParsers.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {t('pages:sipTrunks.form.providerResultParserHelper')}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
          </Card>

          <Card>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                mb: 2,
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="h6">
                  {t('pages:sipTrunks.form.classificationRules')}
                </Typography>
                <Tooltip title={t('pages:sipTrunks.form.classificationRulesHelper')} arrow>
                  <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<Add />}
                onClick={handleAddClassificationRule}
              >
                {t('common:actions.add')}
              </Button>
            </Box>

            {formData.classificationRules.length === 0 ? (
              <Alert severity="info">
                {t('pages:sipTrunks.form.classificationRulesEmpty')}
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {formData.classificationRules.map((rule, index) => (
                  <Paper
                    key={rule.localId}
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 3 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', md: 'center' },
                        justifyContent: 'space-between',
                        gap: 2,
                        mb: 2,
                        flexDirection: { xs: 'column', md: 'row' },
                      }}
                    >
                      <Typography variant="subtitle2">
                        {t('pages:sipTrunks.form.ruleLabel', { number: index + 1 })}
                      </Typography>
                      <Tooltip title={t('common:actions.remove')} arrow>
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveClassificationRule(rule.localId)}
                          aria-label={t('common:actions.remove')}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 2 }}>
                        <TextField
                          fullWidth
                          type="number"
                          label={t('pages:sipTrunks.form.priority')}
                          value={rule.priority}
                          onChange={handleRuleChange(rule.localId, 'priority')}
                          inputProps={{ min: 0 }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <FormControl fullWidth>
                          <InputLabel>{t('pages:sipTrunks.form.phase')}</InputLabel>
                          <Select
                            value={rule.phase}
                            onChange={handleRuleChange(rule.localId, 'phase')}
                            label={t('pages:sipTrunks.form.phase')}
                          >
                            {options?.classificationRulePhases.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <FormControl fullWidth>
                          <InputLabel>{t('pages:sipTrunks.form.preGateOutcome')}</InputLabel>
                          <Select
                            value={rule.preGateOutcome}
                            onChange={handleRuleChange(rule.localId, 'preGateOutcome')}
                            label={t('pages:sipTrunks.form.preGateOutcome')}
                          >
                            {options?.preGateOutcomes.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, md: 2 }}>
                        <TextField
                          fullWidth
                          type="number"
                          label={t('pages:sipTrunks.form.sipStatusCode')}
                          value={rule.sipStatusCode}
                          onChange={handleRuleChange(rule.localId, 'sipStatusCode')}
                          inputProps={{ min: 100, max: 699 }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 2 }}>
                        <TextField
                          fullWidth
                          type="number"
                          label={t('pages:sipTrunks.form.hangupCauseCode')}
                          value={rule.hangupCauseCode}
                          onChange={handleRuleChange(rule.localId, 'hangupCauseCode')}
                          inputProps={{ min: 0, max: 255 }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label={t('pages:sipTrunks.form.dialStatus')}
                          value={rule.dialStatus}
                          onChange={handleRuleChange(rule.localId, 'dialStatus')}
                          placeholder={t('pages:sipTrunks.form.dialStatusPlaceholder')}
                          helperText={t('pages:sipTrunks.form.dialStatusHelper')}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>
            )}
          </CardContent>
          </Card>

          <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('pages:sipTrunks.form.media')}
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('pages:sipTrunks.form.codecs')}</InputLabel>
                  <Select
                    multiple
                    value={formData.codecs}
                    onChange={handleCodecsChange}
                    input={<OutlinedInput label={t('pages:sipTrunks.form.codecs')} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {options?.codecs.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('pages:sipTrunks.form.mediaEncryption')}</InputLabel>
                  <Select
                    value={formData.mediaEncryption}
                    onChange={handleChange('mediaEncryption')}
                    label={t('pages:sipTrunks.form.mediaEncryption')}
                  >
                    {options?.mediaEncryption.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('pages:sipTrunks.form.inboundContext')}</InputLabel>
                  <Select
                    value={formData.inboundContext}
                    onChange={handleChange('inboundContext')}
                    label={t('pages:sipTrunks.form.inboundContext')}
                  >
                    {options?.inboundContexts.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
          </Card>

          <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
              <Typography variant="h6">
                {t('pages:sipTrunks.form.dialingRules')}
              </Typography>
              <Tooltip title={t('pages:sipTrunks.form.dialingRulesHelper')} arrow>
                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label={t('pages:sipTrunks.form.callerId')}
                  value={formData.callerId}
                  onChange={handleChange('callerId')}
                  placeholder={t('pages:sipTrunks.form.callerIdPlaceholder')}
                  helperText={t('pages:sipTrunks.form.callerIdHelper')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label={t('pages:sipTrunks.form.techPrefix')}
                  value={formData.techPrefix}
                  onChange={handleChange('techPrefix')}
                  placeholder="0"
                  helperText={t('pages:sipTrunks.form.techPrefixHelper')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label={t('pages:sipTrunks.form.removeDigits')}
                  value={formData.removeDigits}
                  onChange={handleChange('removeDigits')}
                  placeholder="+"
                  helperText={t('pages:sipTrunks.form.removeDigitsHelper')}
                />
              </Grid>
            </Grid>
          </CardContent>
          </Card>
        </Box>

        <Box
          role="tabpanel"
          hidden={activeFormTab !== 'inbound'}
          sx={{
            display: activeFormTab === 'inbound' ? 'flex' : 'none',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <SipTrunkInboundTab trunkId={trunkId} />
        </Box>
      </Box>
    </Box>
  );
}
